import { ContentfulLivePreview, LIVE_PREVIEW_EDITOR_SOURCE, LivePreviewPostMessageMethods } from '@contentful/live-preview';
import React, { useEffect } from 'react';
import { usePersistedState } from '$utilities/data';


/* ========================================================================= */
/* Helpers */
/* ========================================================================= */
function isValidMessage(event) {
    if (typeof event.data !== 'object' || !event.data) {
        return false;
    }

    if ('source' in event.data && event.data.source === LIVE_PREVIEW_EDITOR_SOURCE) {
        return true;
    }

    return false;
}

/**
 * Recursively finds all values of a given key in a deeply nested object.
 * @param {Record<string, unknown>} obj - The object to search.
 * @param {string} targetKey - The key to search for.
 * @returns {Set<string>} An array of all values found for the given key.
 */
function findAllValues(obj, targetKey) {
    const results = new Set();

    /**
     * @param {{ [s: string]: any; } | ArrayLike<any> | null} value
     */
    function recurse(value) {
        if (Array.isArray(value)) {
            value.forEach(recurse);
        } else if (value !== null && typeof value === 'object') {
            for (const [k, v] of Object.entries(value)) {
                if (k === targetKey) {
                    results.add(v);
                }
                recurse(v);
            }
        }
    }
    recurse(obj);
    return results;
}

function reloadPage() {
    const url = new URL(window.location.href);
    url.searchParams.set('at', new Date().toISOString());
    window.location = url.href;
}



/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const ContentfulLivePreviewOverlay = ({ preview, data, children }) => {
    const { setValue: setUpdating, value: updating } = usePersistedState(false, 'previewUpdatePending');
    const { setValue: setPreviewScrollPosition, value: previewScrollPosition, isReady } = usePersistedState(0, 'previewScrollPosition');

    useEffect(() => {
        if (preview) {
            ContentfulLivePreview.init({ locale: 'en-US' });
            window.addEventListener('message', (event) => {
                if (!isValidMessage(event)) { return; }
                switch (event.data.method) {
                    case LivePreviewPostMessageMethods.ENTRY_SAVED:
                        setTimeout(() => {
                            setUpdating(false);
                            setPreviewScrollPosition(window.scrollY);
                            reloadPage();
                        }, 400);
                        break;
                }
            });

            if (data) {
                const allEntryIds = findAllValues(data, 'cfEntryId');
                if (allEntryIds.size > 0) {
                    ContentfulLivePreview.subscribe('edit', {
                        data: Array.from(allEntryIds).map((id) => ({
                            fields: { '__': '__' },
                            sys: {
                                id
                            }
                        })),
                        callback: () => setUpdating(true, 6000)
                    });
                }
            }
        }
    }, []);


    // Restore scroll position
    useEffect(() => {
        if (isReady && previewScrollPosition) {
            window.scroll({
                top: previewScrollPosition,
                behavior: 'instant'
            });
        }
    }, [isReady, previewScrollPosition]);

    return (
        <>
            {updating && (
                <div className='fixed top-0 left-0 w-full p-10 z-[8888]'>
                    <div className='bg-button-brand-background type-style-eyebrow uppercase text-button-brand-text shadow-md px-10 py-8 rounded'>
                        <p
                            className='loading-dots'
                            role='alert'>
                            {'Update pending'}
                        </p>
                    </div>
                </div>
            )}
            {children}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */
export default ContentfulLivePreviewOverlay;
